import { useQueryClient } from '@tanstack/react-query';
import { useSentryMutation, useSentryQuery } from '../utils/sentryQueryWrapper';
import {
  acceptInvitation,
  activateWorkspace,
  createWorkspace,
  declineInvitation,
  deleteWorkspace,
  fetchDeletionSummary,
  fetchMyInvitations,
  fetchWorkspaces,
  fetchWorkspaceInvitations,
  fetchWorkspaceRoles,
  inviteToWorkspace,
  renameWorkspace,
  revokeWorkspaceInvitation,
} from '@/api';
import type { CreateWorkspacePayload, InvitePayload } from '@/api/workspaceService';
import {
  ActivateWorkspaceResponse,
  InviteResult,
  Workspace,
  WorkspaceDeletionSummary,
  WorkspaceInvitation,
  WorkspaceRole,
} from '@/types';
import { useAuthStore } from '@/stores/authStore';

export const useFetchWorkspaces = () =>
  useSentryQuery<{ object: Workspace[] }, Error>(['workspaces'], () => fetchWorkspaces(), {
    keepPreviousData: true,
  });

export const useFetchMyInvitations = () =>
  useSentryQuery<{ object: WorkspaceInvitation[] }, Error>(
    ['workspace-invitations'],
    () => fetchMyInvitations(),
    { keepPreviousData: true },
  );

/**
 * Switch workspace.
 *
 * 🔴 THE ORDER OF THE FOUR STEPS IS THE WHOLE CORRECTNESS ARGUMENT.
 *
 * 0. CANCEL EVERY IN-FLIGHT QUERY FIRST. A request issued moments before the
 *    switch is still travelling, and it was sent with workspace A's token. If
 *    it resolves AFTER the clear below, React Query writes A's rows into a
 *    cache that is now B's — the exact leak the clear exists to prevent,
 *    arriving a few hundred milliseconds late and looking like a backend bug.
 * 1. Store the new token. The server has already deleted the old one by
 *    the time this resolves, so any request issued before the swap is a 401.
 * 2. Then clear the React Query cache ENTIRELY, not selectively. Every cached
 *    list, summary and detail in the app belongs to the previous workspace, and
 *    they are keyed by endpoint rather than by tenant — so a surviving entry is
 *    not stale data, it is ANOTHER STORE'S data rendered under the new
 *    workspace's name. `invalidateQueries` would repaint from cache before the
 *    refetch lands and show exactly that, for a frame or for as long as the
 *    request takes.
 * 3. Only then navigate. The caller does that, because where to land is a
 *    routing decision, not a data one.
 *
 * ⚠️ Zustand stores holding workspace-scoped selections (order filters, a
 * selected pickup address) are reset by the caller for the same reason: an id
 * from workspace A means nothing in B, and silently carrying it over produces
 * a filter that matches nothing with no explanation.
 */
export const useActivateWorkspace = () => {
  const setToken = useAuthStore((state) => state.setToken);
  const queryClient = useQueryClient();

  return useSentryMutation<{ object: ActivateWorkspaceResponse }, Error, string>(
    (uid: string) => activateWorkspace(uid),
    {
      onMutate: async () => {
        await queryClient.cancelQueries();
      },
      onSuccess: (data) => {
        setToken(data.object.access_token);
        queryClient.clear();
      },
    },
  );
};

export const useAcceptInvitation = () => {
  const queryClient = useQueryClient();

  return useSentryMutation<unknown, Error, string>((uid: string) => acceptInvitation(uid), {
    onSuccess: () => {
      /*
       * 🔴 `['user']` IS THE ONE THAT MATTERS, AND IT WAS THE ONE MISSING.
       *
       * The switcher does not read the ['workspaces'] query at all — it reads
       * `user.workspaces` out of the ['user'] payload (useFetchUser). So a
       * merchant could accept an invitation, or create a workspace, and the
       * switcher would keep showing the old list until a full page reload:
       * every invalidation here fired, and none of them touched the query the
       * component actually renders from. ['profile'] is not that key either.
       *
       * ⭐⭐⭐ INVALIDATE THE KEY THE COMPONENT READS, not the key named after
       * the thing that changed.
       *
       * The active workspace does not change — accepting JOINS, it does not
       * switch — so this stays an invalidate and never a cache clear.
       */
      queryClient.invalidateQueries(['user']);
      queryClient.invalidateQueries(['workspaces']);
      queryClient.invalidateQueries(['workspace-invitations']);
      queryClient.invalidateQueries(['profile']);
    },
  });
};

export const useDeclineInvitation = () => {
  const queryClient = useQueryClient();

  return useSentryMutation<unknown, Error, string>((uid: string) => declineInvitation(uid), {
    onSuccess: () => {
      queryClient.invalidateQueries(['workspace-invitations']);
      // The sidebar's pending badge is derived from the same list, and a
      // declined invitation must stop being counted immediately.
      queryClient.invalidateQueries(['user']);
    },
  });
};

export const useCreateWorkspace = () => {
  const queryClient = useQueryClient();

  // Typed on both sides so the caller can switch into what it just created —
  // an untyped `unknown` forced a cast at the call site, which is where a shape
  // change would have stopped being a compile error.
  return useSentryMutation<{ object: Workspace }, Error, CreateWorkspacePayload>(
    (payload) => createWorkspace(payload),
    {
      onSuccess: () => {
        // ['user'] first, for the same reason as accept: the switcher renders
        // from user.workspaces, so without it a merchant creates a workspace
        // and cannot see it until they reload.
        queryClient.invalidateQueries(['user']);
        queryClient.invalidateQueries(['workspaces']);
        queryClient.invalidateQueries(['profile']);
      },
    },
  );
};

/**
 * The invitations a workspace has SENT — a different list from the caller's own
 * received ones, and keyed separately so accepting one of yours never repaints
 * somebody else's outbox.
 *
 * `enabled` rather than an early return: the panel mounts collapsed, and firing
 * a request per workspace on page load would be N requests nobody asked for.
 */
/**
 * Rename a workspace.
 *
 * ⭐⭐⭐ `['user']` IS THE KEY THE SWITCHER READS — the lesson this file already
 * carries once, above. The header switcher and the "current workspace" tile
 * render from `user.workspaces` out of the ['user'] payload, NOT from the
 * ['workspaces'] query, so invalidating the key named after the thing that
 * changed would repaint the card and leave the header showing the old name
 * until a reload. That was the bug the owner hit the morning of 2026-09-16.
 *
 * Invalidate, never clear: a rename does not change WHICH workspace is active,
 * so nothing in the cache belongs to a different tenant afterwards.
 */
export const useRenameWorkspace = () => {
  const queryClient = useQueryClient();

  return useSentryMutation<{ object: Workspace }, Error, { uid: string; name: string }>(
    ({ uid, name }) => renameWorkspace(uid, name),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['user']);
        queryClient.invalidateQueries(['workspaces']);
        queryClient.invalidateQueries(['profile']);
        // The company profile page shows the same name for the active workspace.
        queryClient.invalidateQueries(['company']);
      },
    },
  );
};

/**
 * The situation report behind the delete confirmation.
 *
 * ⚠️ `enabled` so it fires when the modal OPENS, not on the workspaces page.
 * It counts orders, pickups, remittances and reads the COD ledger for one
 * workspace — running that for every card on page load would be N of the most
 * expensive queries on the page, for a dialog most people never open.
 *
 * `cacheTime: 0` because it is a decision input: a merchant who closes the
 * modal, settles a remittance and reopens it must not be shown the numbers
 * from before they acted.
 */
/**
 * 🔴 `keepPreviousData: false` IS THE WHOLE POINT OF THIS LINE.
 *
 * `defaultQueryOptions` turns `keepPreviousData` ON for every query in the app.
 * That is right for a paginated LIST — page 2 should not blank out while it
 * loads — but this key identifies a different ENTITY, not a different page. So
 * opening the delete dialog on workspace B served workspace A's order count,
 * pickup count and balance, with `isLoading` FALSE because data was present,
 * and the numbers then jumped when B's response landed.
 *
 * On a confirmation that deletes something, showing another workspace's figures
 * is worse than showing a spinner: the merchant reads the wrong numbers and the
 * layout moves under them as they read. `cacheTime`/`staleTime` cannot fix it —
 * keepPreviousData acts on the observer, not the cache.
 */
export const useWorkspaceDeletionSummary = (uid?: string, enabled = true) =>
  useSentryQuery<{ object: WorkspaceDeletionSummary }, Error>(
    ['workspace-deletion-summary', uid],
    () => fetchDeletionSummary(uid as string),
    {
      enabled: Boolean(uid) && enabled,
      cacheTime: 0,
      staleTime: 0,
      keepPreviousData: false,
    },
  );

/**
 * Delete a workspace.
 *
 * ⭐⭐⭐ `['user']` again — the switcher renders from `user.workspaces`, so a
 * deleted workspace would stay in the header until a reload without it. Same
 * lesson as rename and accept, third time in this file.
 *
 * ⚠️ Invalidate, never clear: the caller is NOT in the deleted workspace (the
 * server refuses deleting the active one), so nothing cached belongs to it.
 */
export const useDeleteWorkspace = () => {
  const queryClient = useQueryClient();

  return useSentryMutation<unknown, Error, string>((uid: string) => deleteWorkspace(uid), {
    onSuccess: () => {
      queryClient.invalidateQueries(['user']);
      queryClient.invalidateQueries(['workspaces']);
      queryClient.invalidateQueries(['profile']);
    },
  });
};

export const useFetchWorkspaceInvitations = (workspaceUid?: string, enabled = true) =>
  useSentryQuery<{ object: WorkspaceInvitation[] }, Error>(
    ['workspace-sent-invitations', workspaceUid],
    () => fetchWorkspaceInvitations(workspaceUid as string),
    { enabled: Boolean(workspaceUid) && enabled },
  );

/**
 * The roles this workspace may offer an invitee.
 *
 * ⚠️ Keyed by workspace uid, and it has to be: the Workspaces page can invite
 * into any workspace the merchant staffs, so a single cache entry would serve
 * workspace A's roles into workspace B's dialog — which the server then refuses,
 * leaving the UI unable to explain a rejection of a role it offered.
 */
export const useFetchWorkspaceRoles = (workspaceUid?: string, enabled = true) =>
  useSentryQuery<{ object: WorkspaceRole[] }, Error>(
    ['workspace-roles', workspaceUid],
    () => fetchWorkspaceRoles(workspaceUid as string),
    { enabled: Boolean(workspaceUid) && enabled },
  );

export const useInviteToWorkspace = (workspaceUid: string) => {
  const queryClient = useQueryClient();

  return useSentryMutation<{ object: InviteResult }, Error, InvitePayload>(
    (payload) => inviteToWorkspace(workspaceUid, payload),
    {
      /*
       * ⚠️ SILENT MODE, SO THERE IS EXACTLY ONE ERROR SURFACE (QA U2).
       *
       * `useSentryMutation` calls `handleErrorMessage()` — an Ant `message`
       * banner at the top of the page — for any mutation that does not opt out.
       * The dialog also raises its own toast, so a refusal appeared TWICE, in
       * two different visual languages, in two corners of the screen.
       *
       * The dialog's toast is the one that stays: it is what the rest of this
       * feature uses, and it carries the server's own sentence. Scoped to this
       * mutation rather than changed globally — every other call site in the
       * product still relies on that banner.
       */
      defaultErrorMessage: '',
      onSuccess: () => {
        queryClient.invalidateQueries(['workspace-sent-invitations', workspaceUid]);
      },
    },
  );
};

export const useRevokeWorkspaceInvitation = (workspaceUid: string) => {
  const queryClient = useQueryClient();

  return useSentryMutation<unknown, Error, string>(
    (invitationUid) => revokeWorkspaceInvitation(workspaceUid, invitationUid),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['workspace-sent-invitations', workspaceUid]);
        // The invitee's own badge counts this row too.
        queryClient.invalidateQueries(['workspace-invitations']);
      },
    },
  );
};
